import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Projects.css';

/* Fallback image */
import img_mountain from '../assets/image/mountain_village.png';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/projects/`;

function Projects() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProjects = async () => {
      try {
        const res = await fetch(API_URL, {
          headers: {
            'Accept-Language': i18n.language || 'fr'
          }
        });
        if (res.ok) {
          const data = await res.json();
          // Format data to have clean arrays of image URLs
          const formatted = data.map(proj => ({
            ...proj,
            images: proj.images && proj.images.length > 0
              ? proj.images.map(imgObj => imgObj.image)
              : [img_mountain]
          }));
          setProjects(formatted);
        }
      } catch (err) {
        console.error("Erreur de récupération des projets:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [i18n.language]);

  if (loading) {
    return (
      <div className="projects-page">
        {/* ── HERO BANNER — affiché immédiatement ── */}
        <section
          className="ap-hero"
          style={{ backgroundImage: `url(${img_mountain})` }}
        >
          <div className="ap-hero-overlay"></div>
          <div className="ap-hero-content">
            <span className="ap-hero-badge">{t('projects.badgeHero')}</span>
            <h1 className="ap-hero-title">
              {t('projects.title1')} <span className="keyword">{t('projects.titleKeyword')}</span>
            </h1>
            <p className="ap-hero-sub">{t('projects.subHero')}</p>
          </div>
          <div className="ap-hero-wave">
            <svg viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path fill="var(--background-solid)" d="M0,100 L0,40 C360,100 1080,0 1440,60 L1440,100 Z"></path>
            </svg>
          </div>
        </section>
        <section className="proj-section">
          <div className="proj-loading-container" style={{ minHeight: '40vh' }}>
            <div className="proj-spinner"></div>
            <p style={{ color: '#666', fontWeight: 600 }}>{t('projects.loading')}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="projects-page">
      {/* ── HERO BANNER ── */}
      <section
        className="ap-hero"
        style={{ backgroundImage: `url(${img_mountain})` }}
      >
        <div className="ap-hero-overlay"></div>
        <div className="ap-hero-content">
          <span className="ap-hero-badge">{t('projects.badgeHero')}</span>
          <h1 className="ap-hero-title">
            {t('projects.title1')} <span className="keyword">{t('projects.titleKeyword')}</span>
          </h1>
          <p className="ap-hero-sub">
            {t('projects.subHero')}
          </p>
        </div>
        <div className="ap-hero-wave">
          <svg viewBox="0 0 1440 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="var(--background-solid)"
              d="M0,100 L0,40 C360,100 1080,0 1440,60 L1440,100 Z"
            ></path>
          </svg>
        </div>
      </section>

      {/* ── MAIN GRID ── */}
      <section className="proj-section">
        <div className="proj-container">

          {/* ── Définition du terme ── */}
          <div className="proj-definition-header">
            <span className="proj-definition-label">{t('projects.introBadge')}</span>
            <h2 className="proj-definition-title">
              {t('projects.introTitle1')}
              <span className="keyword">{t('projects.introTitleKeyword')}</span>
            </h2>
          </div>

          <div className="proj-definition">
            <div className="proj-definition-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 16v-4M12 8h.01" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <p className="proj-definition-text">
                <strong>TIWIZI</strong> — {t('projects.introText')}
              </p>
            </div>
          </div>

          <div className="proj-grid">
            {projects.length === 0 ? (
              <div className="proj-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
                  <path d="M12 8V12" />
                  <path d="M12 16H12.01" />
                </svg>
                <p>{t('projects.empty')}</p>
              </div>
            ) : (
              projects.map(proj => (
                <div
                  key={proj.id}
                  className="proj-card"
                  onClick={() => navigate(`/projets/${proj.id}`)}
                >
                  <div className="proj-card-image-area">
                    <div className="proj-card-image-stage">
                      <img
                        src={proj.images[0]}
                        alt={proj.titre}
                        className="proj-card-img-contain"
                      />
                    </div>
                  </div>

                  <div className="proj-card-content">
                    <h3 className="proj-card-title">{proj.titre}</h3>
                    <div className="proj-card-footer">
                      <span>{t('projects.hint') || 'Voir les détails'}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Projects;
